import React, { useMemo, useState } from "react"
import Chart from "react-apexcharts"
import moment from "moment"
import { getWeekTooltipLabel, getSameWeekPreviousYear } from "utils/dateUtil"

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "chronological", icon: "⇅", label: "Sort",       color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending",     icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending",    icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "10px", padding: "7px 14px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "120px",
      }}
    >
      <span style={{ fontSize: "16px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "18px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.2 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.4 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const ServiceLevelChart = ({
  data = [],
  previousData = [],
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological" ? "ascending"
      : sortOrder === "ascending" ? "descending"
      : "chronological"
    setSortOrder(next)
  }

  const currentYear = data.length ? moment(data[0].date).year() : moment().year()
  const previousYear = previousData.length ? moment(previousData[0].date).year() : currentYear - 1

  // ── Union of all ISO weeks ───────────────────────────────────────────────
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  // ── Current year maps ────────────────────────────────────────────────────
  const currentByWeek = useMemo(() => {
    const map = {}
    data.forEach(i => { map[moment(i.date).isoWeek()] = Number(i.level) || 0 })
    return map
  }, [data])

  const dateByWeek = useMemo(() => {
    const map = {}
    data.forEach(i => { map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY") })
    return map
  }, [data])

  // ── Previous year maps ───────────────────────────────────────────────────
  const prevByWeek = useMemo(() => {
    const map = {}
    previousData.forEach(i => { map[moment(i.date).isoWeek()] = Number(i.level) || 0 })
    return map
  }, [previousData])

  const prevDateByWeek = useMemo(() => {
    const map = {}
    previousData.forEach(i => { map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY") })
    return map
  }, [previousData])

  // ── Apply sort order ────────────────────────────────────────────────────
  const displayWeeks = useMemo(() => {
    if (sortOrder === "ascending") {
      return [...allWeeks].sort((a, b) => {
        const vA = currentByWeek[a] ?? 0
        const vB = currentByWeek[b] ?? 0
        return vA !== vB ? vA - vB : a - b
      })
    }
    if (sortOrder === "descending") {
      return [...allWeeks].sort((a, b) => {
        const vA = currentByWeek[a] ?? 0
        const vB = currentByWeek[b] ?? 0
        return vA !== vB ? vB - vA : a - b
      })
    }
    return allWeeks
  }, [allWeeks, currentByWeek, sortOrder])

  // ── Build series from displayWeeks ──────────────────────────────────────
  const currentValues = displayWeeks.map(w => currentByWeek[w] ?? null)
  const prevValues    = displayWeeks.map(w => prevByWeek[w] ?? null)
  const categories    = displayWeeks.map((_, i) => `${i + 1}`)

  const series = [
    { name: `Current Year (${currentYear})`,   type: "column", data: currentValues },
    { name: `Previous Year (${previousYear})`, type: "area",   data: prevValues },
  ]

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 400 },
    },
    stroke: {
      width: [0, 2.5],
      curve: "smooth",
      dashArray: [0, 6],
    },
    markers: {
      size: [0, 3],
      colors: ["#fff"],
      strokeColors: ["#F87171"],
      strokeWidth: 2,
      hover: { size: 5 },
    },
    dataLabels: { enabled: false },
    fill: {
      opacity: [1, 0.2],
      type: ["solid", "gradient"],
      gradient: {
        type: "vertical",
        shadeIntensity: 0.5,
        opacityFrom: 0.25,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    colors: ["#A78BFA", "#F87171"],
    plotOptions: {
      bar: { columnWidth: "55%", borderRadius: 4, borderRadiusApplication: "end" },
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
        formatter: val =>
          val !== null && val !== undefined
            ? `${Number(val).toLocaleString("en-GB")}%`
            : "N/A",
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "dark",
      style: { fontSize: "12px" },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum           = displayWeeks[dataPointIndex]
          const currentDate       = dateByWeek[weekNum]
          const prevDate          = prevDateByWeek[weekNum]
          const resolvedPrevDate  = prevDate || (currentDate ? getSameWeekPreviousYear(currentDate) : null)
          const currentLabel      = getWeekTooltipLabel(dataPointIndex, currentDate)
          const prevLabel         = getWeekTooltipLabel(dataPointIndex, resolvedPrevDate)
          return `Current: ${currentLabel || "N/A"}<br/>Previous: ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? `${Number(val).toLocaleString("en-GB")}%`
            : "N/A",
      },
    },
    legend: { show: false },
    states: {
      hover: { filter: { type: "lighten", value: 0.08 } },
      active: { filter: { type: "darken", value: 0.1 } },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: "1px solid #A78BFA22",
        boxShadow: "0 4px 24px #A78BFA14, 0 1px 4px rgba(0,0,0,0.04)",
        position: "relative",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0,
          height: "3px",
          background: "linear-gradient(90deg, #A78BFA, #A78BFA88)",
          borderRadius: "16px 16px 0 0",
        }}
      />

      <div style={{ padding: "20px 16px 8px" }}>
        <div
          style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            gap: "8px", marginBottom: "4px", padding: "0 8px",
            flexWrap: "wrap",
          }}
        >
          {/* Title */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px", height: "8px", borderRadius: "50%",
                background: "#A78BFA", boxShadow: "0 0 8px #A78BFA88",
              }}
            />
            <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#1e293b" }}>
              Service Level
            </h4>
          </div>

          {/* Right side: Sort Order button + Legend */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            {/* Sort Order cycling button */}
            <SortButton sortOrder={sortOrder} onToggle={handleToggle} />

            {/* Legend */}
            <div style={{ display: "flex", gap: "16px" }}>
              {[
                { color: "#A78BFA", label: `Current Year (${currentYear})`,   dash: false },
                { color: "#F87171", label: `Previous Year (${previousYear})`, dash: true  },
              ].map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {l.dash ? (
                    <span style={{ width: 16, height: 2, borderTop: `2px dashed ${l.color}`, display: "inline-block" }} />
                  ) : (
                    <span style={{ width: 16, height: 10, background: l.color, borderRadius: "2px", display: "inline-block" }} />
                  )}
                  <small style={{ color: "#1e293b", fontWeight: 600, fontSize: "11px" }}>
                    {l.label}
                  </small>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Chart options={options} series={series} type="line" height={450} />
      </div>
    </div>
  )
}

export default ServiceLevelChart
